import { useTranslation } from 'react-i18next'
import { useState } from 'react'

import styles from '../ReassignDonationModal.module.scss'

import { AcknowledgeModal, ConfirmationModal, Modal, Text } from '@percent/lemonade'
import { RemoveFromReassignmentQueueModalProps } from './RemoveFromReassignmentQueueModal.types'
import { useMutation } from '@percent/admin-dashboard/common/hooks'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { isApiError } from '@percent/admin-dashboard/common/utility/isApiError'

export function RemoveFromReassignmentQueueModal({
  open,
  onClose,
  selectedDonation
}: RemoveFromReassignmentQueueModalProps) {
  const { donationsService } = useServices()

  const [successDialogState, setSuccessDialogState] = useState(false)
  const [errorDialogState, setErrorDialogState] = useState(false)

  function setError() {
    setErrorDialogState(true)
    onClose()
  }

  function setSuccess() {
    setSuccessDialogState(true)
    onClose()
  }

  const { t } = useTranslation()

  const [{ isLoading: isRemoveFromReassignmentQueueLoading, error }, { apiFunc: removeFromReassignmentQueueApiFunc }] =
    useMutation(
      donationsService.removeDonationFromReassignmentQueue,
      () => setSuccess(),
      () => setError()
    )

  const handleOnErrorClose = () => {
    setErrorDialogState(false)
  }

  const handleSuccessOnClose = () => {
    setSuccessDialogState(false)
  }

  const successOrErrorModal =
    successDialogState && !errorDialogState ? (
      <Modal open={successDialogState} onClose={handleSuccessOnClose}>
        <AcknowledgeModal
          result="positive"
          title={t('dialog.removeFromReassignmentQueue.success.title')}
          description={t('dialog.removeFromReassignmentQueue.success.description')}
          buttonText={t('button.close')}
          handleClose={handleSuccessOnClose}
          viewTestId="success-donation-reassign-modal"
        />
      </Modal>
    ) : (
      <Modal open={errorDialogState} onClose={handleOnErrorClose}>
        <AcknowledgeModal
          result="negative"
          title={t('dialog.removeFromReassignmentQueue.error.title')}
          buttonText={t('button.close')}
          handleClose={handleOnErrorClose}
          viewTestId="error-donation-reassign-modal"
        >
          <div className={styles.errorPage}>
            <Text size="medium">{t('dialog.removeFromReassignmentQueue.error.description')}</Text>
            {error && isApiError(error) && (
              <div className={styles.errorDetails}>
                <Text size="small">{error.message}</Text>
                {error.reasons && (
                  <ul>
                    {error.reasons.map((reason, i) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <li key={`reason-${i}`}>
                        <Text size="small">{reason}</Text>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        </AcknowledgeModal>
      </Modal>
    )

  return (
    <>
      {successOrErrorModal}
      <Modal open={open} onClose={onClose}>
        <ConfirmationModal
          title={t('confirmationModal.removeFromReassignmentQueue.title')}
          primaryButtonText={t('confirmationModal.removeFromReassignmentQueue.primaryButtonText')}
          secondaryButtonText={t('confirmationModal.removeFromReassignmentQueue.secondaryButtonText')}
          handleClose={onClose}
          handleSubmit={() => {
            return selectedDonation ? removeFromReassignmentQueueApiFunc({ donationId: selectedDonation.id }) : null
          }}
          loading={isRemoveFromReassignmentQueueLoading}
        >
          {t('confirmationModal.removeFromReassignmentQueue.description')}
        </ConfirmationModal>
      </Modal>
    </>
  )
}
